import clsx from 'clsx'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { mediaQueryDown, mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import IconButton from 'components/Luxkit/Button/IconButton'
import LineTimesIcon from 'components/Luxkit/Icons/line/LineTimesIcon'
import Heading from 'components/Luxkit/Typography/Heading'
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import styled, { css, keyframes } from 'styled-components'
import Pane from '../Pane'
import { detectGiftCardNumber } from 'api/promo'
import ClickableListItem from 'components/Luxkit/List/ClickableListItem'
import LineKeyboardAltIcon from 'components/Luxkit/Icons/line/LineKeyboardAltIcon'
import LineArrowRightIcon from 'components/Luxkit/Icons/line/LineArrowRightIcon'
import Image from 'components/Common/Image'
import LineCameraIcon from 'components/Luxkit/Icons/line/LineCameraIcon'
import useToggle from 'hooks/useToggle'
import LoadingIndicator from '../Loading/LoadingIndicator'
import Group from 'components/utils/Group'
import LineRotate360Icon from 'components/Luxkit/Icons/line/LineRotate360Icon'
import Clickable from '../Clickable'
import Breakpoint from '../Breakpoint'
import TextButton from 'components/Luxkit/Button/TextButton'
import * as Analytics from 'analytics/analytics'
import AnalyticsPageContext from 'contexts/Analytics/analyticsPageContext'

type FacingMode = 'user' | 'environment' | 'left' | 'right'

// See https://developer.mozilla.org/en-US/docs/Web/API/MediaTrackConstraints#constraindomstring
function parseFacingMode(facingMode: any): FacingMode | undefined {
  if (typeof facingMode === 'string') { return facingMode as FacingMode }
  if (Array.isArray(facingMode)) { return facingMode[0] as FacingMode }
  return facingMode?.exact as FacingMode | undefined
}

function getFacingMode(camera: MediaDeviceInfo): FacingMode | 'unknown' {
  // getCapabilities is not supported in all browsers yet
  if ('getCapabilities' in camera) {
    const capabilities = (camera.getCapabilities as any)()
    return parseFacingMode(capabilities.facingMode) ?? 'unknown'
  }
  // Try to infer from the camera label
  if (camera.label.includes('front')) {
    return 'user'
  } else if (camera.label.includes('back')) {
    return 'environment'
  }
  return 'unknown'
}

const Root = styled(Pane)`
  background-color: black;

  ${mediaQueryDown.mobile} {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
`

const HeadingContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: ${rem(20)};
  padding-left: ${rem(30)};
`

const StyledHeading = styled(Heading)`
  line-height: ${rem(40)};
`

const MessageContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  gap: ${rem(16)};
  height: 100%;
  padding: ${rem(20)};

  & img {
    max-width: 90%;
  }

  ${mediaQueryUp.tablet} {
    min-height: ${rem(300)};
    height: auto;

    & img {
      max-width: 70%;
    }
  }
`

const VideoContainer = styled.div`
  position: relative;
  display: none;
  flex: 1;

  &.show {
    display: flex;
    align-items: center;
  }
`
const Video = styled.video`
  max-width: 100%;
`

const ButtonContainer = styled.div`
  display: flex;
  position: absolute;
  bottom: ${rem(20)};
  padding-inline: ${rem(20)};
  width: 100%;
`

const cameraUIButtonStyle = css`
  display: inline-flex;
  align-items: center;
  text-align: center;
  justify-content: center;
  position: relative;
  transition: color 0.2s, background-color 0.2s, border-color 0.2s;
  border: 1px solid transparent;
  touch-action: manipulation;
  cursor: pointer;
  user-select: none;
  padding: 0;
  flex-shrink: 0;
  width: ${rem(64)};
  height: ${rem(64)};
  border-radius: ${props => props.theme.borderRadius.round};

  > svg:first-of-type {
    flex-shrink: 0;
    height: ${rem(32)};
    width: ${rem(32)};
  }

  ${mediaQueryUp.tablet} {
    width: ${rem(48)};
    height: ${rem(48)};

    > svg:first-of-type {
      height: ${rem(24)};
      width: ${rem(24)};
    }
  }
`

const ShutterButton = styled(Clickable)`
  ${cameraUIButtonStyle}
  background-color: white;
  /* Needs a border to ensure the button is clear even against a white webcam image */
  border: 1px solid ${props => props.theme.palette.neutral.default.eight};
`

const SwitchCameraButton = styled(Clickable)`
  ${cameraUIButtonStyle}
  background-color: #333333bb;
`

const FlexGroup = styled(Group)`
  flex: 1;
`

const flashEffect = keyframes`
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
`

const ProcessingImageContainer = styled.div`
  width: 100%;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: white;
    animation: ${flashEffect} 0.5s ease-out forwards;
    z-index: 1;
  }
`

const LoadingIndicatorContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
`

const ProcessingImage = styled.img`
  width: 100%;
`

const Footer = styled.div`
  padding: ${rem(20)};

  ${mediaQueryUp.tablet} {
    padding: ${rem(16)} ${rem(24)} ${rem(24)};
  }
`

const TransparentListItem = styled(ClickableListItem)`
  background-color: transparent;
`

interface Props {
  onSuccess: (code: string, pin: string | undefined) => void
  onCancel: () => void
}

function ScanCode({ onSuccess, onCancel }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const [cameraAccessState, setCameraAccessState] = useState<'prompt' | 'granted' | 'denied' | 'error' | 'not_found'>('prompt')
  const { value: isError, on: setError, off: clearError } = useToggle()
  const [processingImage, setProcessingImage] = useState<string | null>(null)
  const [cameras, setCameras] = useState<Array<MediaDeviceInfo> | undefined>()

  const analyticsPage = useContext(AnalyticsPageContext)

  const hasMultiCameras = cameras && cameras.length > 1

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach((track) => {
      track.stop()
    })
  }

  const startCamera = useCallback(async() => {
    try {
      stopCamera()
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' } } })
      streamRef.current = stream
      setCameraAccessState('granted')
      if (videoRef.current) {
        videoRef.current.srcObject = stream
      }

      // Get list of available cameras
      const devices = await navigator.mediaDevices.enumerateDevices()
      const cameras = devices.filter((device) => device.kind === 'videoinput')
      setCameras(cameras)

      return true
    } catch (e) {
      if (e.name === 'NotAllowedError') {
        setCameraAccessState('denied')
      } else if (e.name === 'NotFoundError') {
        setCameraAccessState('not_found')
      } else {
        setCameraAccessState('error')
      }
      return false
    }
  }, [])

  const tryCameraAgain = useCallback(async() => {
    setCameraAccessState('prompt')
    startCamera()
  }, [startCamera])

  const switchCamera = useCallback(async() => {
    if (!cameras) { return }

    const currentCameraLabel = streamRef.current?.getVideoTracks()[0].label
    const currentCameraIndex = cameras.findIndex((camera) => camera.label === currentCameraLabel)

    if (currentCameraIndex === -1) { return }

    const currentCamera = cameras[currentCameraIndex]
    const currentFacingMode = getFacingMode(currentCamera)

    const camerasAfterCurrent = cameras.slice(currentCameraIndex + 1).concat(cameras.slice(0, currentCameraIndex + 1))

    const nextCamera = camerasAfterCurrent.find((camera) => {
      return getFacingMode(camera) !== currentFacingMode
    }) ?? camerasAfterCurrent[0]

    stopCamera()
    const stream = await navigator.mediaDevices.getUserMedia({
      video: {
        deviceId: nextCamera.deviceId,
      },
    })
    streamRef.current = stream
    if (videoRef.current) {
      videoRef.current.srcObject = stream
    }
  }, [cameras])

  useEffect(() => {
    startCamera()

    return stopCamera
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleImage = useCallback(async(dataURL: string) => {
    const imageBase64 = dataURL.replace(/^.*?,/, '')
    setProcessingImage(dataURL)
    try {
      const result = await detectGiftCardNumber(imageBase64)
      onSuccess(result.cardNumber, result.accessCode)
    } catch {
      setError()
    } finally {
      setProcessingImage(null)
    }
  }, [onSuccess, setError])

  const takePhoto = useCallback(() => {
    if (videoRef.current) {
      const canvas = document.createElement('canvas')
      const video = videoRef.current
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      const context = canvas.getContext('2d')

      if (context) {
        Analytics.trackClientEvent({
          subject: 'scan_gift_card',
          action: 'take_photo',
          category: analyticsPage,
          type: 'interaction',
        })
        context.drawImage(video, 0, 0, canvas.width, canvas.height)
        const dataURL = canvas.toDataURL('image/png')
        handleImage(dataURL)
      }
    }
  }, [analyticsPage, handleImage])

  const onClickBack = useCallback(() => {
    Analytics.trackClientEvent({
      subject: 'scan_gift_card',
      action: 'enter_details_manually',
      category: analyticsPage,
      type: 'interaction',
    })
    onCancel()
  }, [analyticsPage, onCancel])

  return (
    <Root paletteType="inverse">
      <HeadingContainer>
        <StyledHeading variant="heading4" colour="neutral-one">Scan gift card</StyledHeading>

        <IconButton kind="tertiary" onClick={onClickBack}>
          <LineTimesIcon colour="neutral-one"/>
        </IconButton>
      </HeadingContainer>

      {cameraAccessState === 'prompt' && (
        <MessageContainer>
          <BodyText variant="large" colour="neutral-six">Waiting for camera access...</BodyText>
        </MessageContainer>
      )}
      {cameraAccessState === 'not_found' && (
        <MessageContainer>
          <BodyText variant="large" colour="neutral-one">Camera not found</BodyText>
        </MessageContainer>
      )}
      {cameraAccessState === 'error' && (
        <MessageContainer>
          <BodyText variant="large" colour="neutral-one">Error getting camera access</BodyText>
          <TextButton kind="primary" onClick={tryCameraAgain}>Try again</TextButton>
        </MessageContainer>
      )}
      {cameraAccessState === 'denied' && (
        <MessageContainer>
          <BodyText variant="large" colour="neutral-one">
            Camera access is required to scan gift cards. Please grant camera access for this website in your browser settings and try again.
          </BodyText>
          <BodyText variant="large" colour="neutral-one">
            In most browsers, website settings can be found via a button at the left end of the URL bar. For example:
          </BodyText>
          <Breakpoint max="mobile">
            <Image id="sitesettings-camera-permission-mobile" format="png" />
          </Breakpoint>
          <Breakpoint min="tablet">
            <Image id="sitesettings-camera-permission" format="png" />
          </Breakpoint>
          <TextButton kind="primary" onClick={tryCameraAgain}>Try again</TextButton>
        </MessageContainer>
      )}
      {isError && (
        <MessageContainer>
          <BodyText variant="large" colour="neutral-one">Unable to scan gift card details. Please make sure your gift card is clearly visible to the camera.</BodyText>
        </MessageContainer>
      )}

      <VideoContainer className={clsx({ show: cameraAccessState === 'granted' && !isError && !processingImage })}>
        <Video
          ref={videoRef}
          autoPlay
          playsInline
          controlsList="nodownload nofullscreen noremoteplayback"
          disablePictureInPicture
          disableRemotePlayback
          x-webkit-airplay="deny"
        />

        <ButtonContainer>
          <FlexGroup direction="horizontal"></FlexGroup>
          <ShutterButton onClick={takePhoto}>
            <LineCameraIcon />
          </ShutterButton>
          <FlexGroup direction="horizontal" horizontalAlign="end">
            {hasMultiCameras && (
              <SwitchCameraButton onClick={switchCamera}>
                <LineRotate360Icon colour="neutral-one" />
              </SwitchCameraButton>
            )}
          </FlexGroup>
        </ButtonContainer>
      </VideoContainer>

      {processingImage && <ProcessingImageContainer>
        <ProcessingImage src={processingImage} alt="" />
        <LoadingIndicatorContainer>
          <LoadingIndicator inline />
        </LoadingIndicatorContainer>
      </ProcessingImageContainer>}

      <Footer>
        {isError && <TransparentListItem
          startIcon={<LineCameraIcon colour="neutral-one" />}
          endIcon={<LineArrowRightIcon colour="neutral-one" />}
          onClick={clearError}
          title="Try again"
        />}
        <TransparentListItem
          startIcon={<LineKeyboardAltIcon colour="neutral-one" />}
          endIcon={<LineArrowRightIcon colour="neutral-one" />}
          onClick={onClickBack}
          title="Enter details manually"
        />
      </Footer>
    </Root>
  )
}

export default ScanCode
